import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useWalletStore } from '@/stores/useWalletStore'
import { usePairsStore } from '@/stores/usePairsStore'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { getCoinIcon } from '@/lib/common'
import { toFixed } from '@/lib/decimal'
import { sleep } from '@/lib/common'
import type { LiquidityItem } from './MyLiquidity'

// LP token symbol from pair ID: LPTTR + zero-padded ID
function idToSymbol(id: number): string {
  return `LPTTR${String(id).padStart(3, '0')}`
}

export function PoolReduce({ item }: { item: LiquidityItem }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { session } = useWalletStore()
  const { fetchPairs } = usePairsStore()
  const { transact, loading: txLoading } = useEosTransaction()

  const [percent, setPercent] = useState(0)

  const reduceCount = useMemo(() => {
    return Math.floor(item.userToken * (percent / 100))
  }, [item.userToken, percent])

  const getCoin0 = useMemo(() => {
    const ratio = percent / 100
    return toFixed(parseFloat(item.token0Quantity) * ratio, item.token0.precision)
  }, [percent, item])

  const getCoin1 = useMemo(() => {
    const ratio = percent / 100
    return toFixed(parseFloat(item.token1Quantity) * ratio, item.token1.precision)
  }, [percent, item])

  const handleSubmit = async () => {
    if (!session || reduceCount <= 0) return

    const account = session.account
    const symbol = idToSymbol(item.pair.id)
    const quantity = `${reduceCount} ${symbol}`

    const actions = [{
      account: 'lptoken.ttr',
      name: 'transfer',
      authorization: [{ actor: account.name, permission: account.authority || 'active' }],
      data: {
        from: account.name,
        to: 'swap.ttr',
        quantity,
        memo: 'withdraw',
      },
    }]

    const result = await transact(actions, t('swap.removeLiquiditySuccess'))
    if (result) {
      await sleep(500)
      await fetchPairs()
      navigate({ to: '/pool' })
    }
  }

  const quickPercentages = [25, 50, 75, 100]

  return (
    <div className="w-full max-w-[460px] mx-auto px-4 py-6 md:py-10 animate-trade-enter">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate({ to: '/pool' })} className="p-1.5 rounded-full hover:bg-[#edf5ed] transition-colors">
          <ArrowLeft className="h-5 w-5 text-foreground" />
        </button>
        <h2 className="text-[20px] font-bold text-foreground">{t('swap.removeLiquidity')}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-[#e5e5e5] p-5 space-y-5">
        {/* Pair info */}
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            <img src={getCoinIcon(item.token0.contract, item.token0.coin)} className="w-[32px] h-[32px] rounded-full bg-gray-100 border-2 border-white" />
            <img src={getCoinIcon(item.token1.contract, item.token1.coin)} className="w-[32px] h-[32px] rounded-full bg-gray-100 border-2 border-white" />
          </div>
          <span className="text-[16px] font-semibold text-foreground">
            {item.token0.coin}/{item.token1.coin}
          </span>
        </div>

        {/* Available */}
        <div className="text-[13px] text-nav">
          {t('swap.availableCertificate')}: <span className="text-foreground font-semibold">{item.userToken}</span>
        </div>

        {/* Percentage display */}
        <div className="text-center text-[40px] font-bold text-primary">{percent}%</div>

        {/* Slider */}
        <input
          type="range"
          min={0}
          max={100}
          value={percent}
          onChange={(e) => setPercent(Number(e.target.value))}
          className="w-full accent-primary"
        />

        {/* Quick buttons */}
        <div className="flex gap-2">
          {quickPercentages.map((p) => (
            <button
              key={p}
              onClick={() => setPercent(p)}
              className={`flex-1 h-[36px] rounded-full text-[14px] font-semibold transition-colors ${
                percent === p
                  ? 'bg-primary text-white'
                  : 'bg-[#edf5ed] text-primary hover:bg-primary/20'
              }`}
            >
              {p}%
            </button>
          ))}
        </div>

        {/* Withdraw assets */}
        <div className="bg-[#f5f6fa] rounded-xl px-4 py-3 space-y-2 text-[13px]">
          <div className="text-nav mb-2">{t('swap.withdrawAssets')}</div>
          <div className="flex justify-between">
            <span className="text-foreground">{item.token0.coin}</span>
            <span className="text-foreground font-semibold">{getCoin0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground">{item.token1.coin}</span>
            <span className="text-foreground font-semibold">{getCoin1}</span>
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={reduceCount <= 0 || txLoading}
          className="w-full h-[48px] rounded-full text-[16px] font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {txLoading ? t('public.loading') : t('public.confirm')}
        </button>
      </div>
    </div>
  )
}
